import { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { store } from "../../store";
import { ROLES } from "../helpers/constants";
import {
  decryptPayload,
  isBasicPlanOnlyCustomer,
  isMonitoringTierCustomer,
  resolveCustomerProfileCategory,
} from "../helpers/utils";
import { subscriptionAction } from "../../redux/slices/subscription/subscriptionSlice";

const SUBSCRIPTION_FETCH_LIMIT = 500;

let subscriptionBootstrapKey: string | null = null;

const getSubscriptionBootstrapKey = (
  isSecondaryUser: boolean,
  parentId: unknown,
) => {
  const token = localStorage.getItem("authToken") || "anonymous";
  return `${token}:${isSecondaryUser ? String(parentId ?? "") : "primary"}`;
};

export function resetCustomerSubscriptionBootstrap() {
  subscriptionBootstrapKey = null;
}

export function useBasicPlanOnlyCustomer() {
  const dispatch = useDispatch();
  const { subscriptionData, subscriptionLoader, subscriptionFetchComplete } =
    useSelector((state: any) => state?.subscription || {});
  const { userProfileDetails, loginAsSpouse: parentId } = useSelector(
    (state: any) => state?.userProfile || {},
  );
  const { propertiesData } = useSelector((state: any) => state?.properties || {});

  const isSecondaryUser = Boolean(parentId);
  const subscriptionsLoaded = Boolean(subscriptionFetchComplete);

  useEffect(() => {
    const roles = decryptPayload(localStorage.getItem("roles")) || [];
    if (
      !roles.includes(ROLES.customer) &&
      !roles.includes(ROLES.secondaryUser)
    ) {
      return;
    }

    const bootstrapKey = getSubscriptionBootstrapKey(isSecondaryUser, parentId);
    const subscriptionState = store.getState()?.subscription ?? {};

    if (subscriptionState.subscriptionFetchComplete) {
      subscriptionBootstrapKey = bootstrapKey;
      return;
    }

    if (subscriptionBootstrapKey === bootstrapKey) {
      return;
    }

    subscriptionBootstrapKey = bootstrapKey;

    const basePath = roles.includes(ROLES.serviceProvider)
      ? "service-provider"
      : "customer";

    const url = isSecondaryUser
      ? `${basePath}/property-subscription/${parentId}?limit=${SUBSCRIPTION_FETCH_LIMIT}&offset=0`
      : `${basePath}/property-subscription?limit=${SUBSCRIPTION_FETCH_LIMIT}&offset=0`;

    dispatch(
      subscriptionAction.getSubscriptionData({
        url,
        data: {},
        token: true,
      }),
    );
  }, [dispatch, isSecondaryUser, parentId]);

  const tierContext = useMemo(() => {
    const profileCategory = resolveCustomerProfileCategory({
      profileCategory: userProfileDetails?.data?.category,
      profileProductTier: userProfileDetails?.data?.product_tier,
      propertiesData,
    });
    const subscriptions =
      subscriptionData?.success && Array.isArray(subscriptionData?.data)
        ? subscriptionData.data
        : [];

    return { profileCategory, subscriptions };
  }, [propertiesData, subscriptionData, userProfileDetails]);

  const isBasicPlanOnly = useMemo(
    () =>
      isBasicPlanOnlyCustomer({
        subscriptions: tierContext.subscriptions,
        profileCategory: tierContext.profileCategory,
        subscriptionsLoaded,
      }),
    [subscriptionsLoaded, tierContext],
  );

  const isMonitoringTierOnly = useMemo(
    () =>
      isMonitoringTierCustomer({
        subscriptions: tierContext.subscriptions,
        profileCategory: tierContext.profileCategory,
        subscriptionsLoaded,
      }),
    [subscriptionsLoaded, tierContext],
  );

  return {
    isBasicPlanOnly,
    isMonitoringTierOnly,
    subscriptionsLoaded,
    subscriptionLoader: subscriptionLoader && !subscriptionsLoaded,
  };
}

export default useBasicPlanOnlyCustomer;
